/**
 * Feedback API Client
 * Sprint 1, Task 11 & 12: Feedback Collection & Storage
 */

import { getAPIClient } from './client';

export interface FeedbackSubmission {
  query_id: string;
  project_id: string;
  feedback_type: 'helpful' | 'not_helpful' | 'report_issue';
  rating?: number;
  issue_category?: string;
  comment?: string;
  answer_relevance_score?: number;
  accuracy_score?: number;
  completeness_score?: number;
  citation_quality_score?: number;
  timestamp: string;
}

export interface FeedbackStats {
  total_feedback: number;
  helpful_count: number;
  not_helpful_count: number;
  avg_rating: number;
  helpfulness_rate: number;
  recent_issues: Array<{
    id: string;
    issue_category: string;
    comment: string;
    created_at: string;
  }>;
}

/**
 * Submit user feedback
 */
export async function submitFeedbackAPI(feedback: FeedbackSubmission): Promise<void> {
  const client = getAPIClient();
  
  try {
    await client.post('/feedback/submit', feedback);
  } catch (error: any) {
    console.error('Failed to submit feedback:', error);
    throw new Error(`Feedback submission failed: ${error.response?.data?.detail || error.message}`);
  }
}

/**
 * Get feedback for a specific query
 */
export async function getQueryFeedback(queryId: string): Promise<FeedbackSubmission | null> {
  const client = getAPIClient();
  
  try {
    const response = await client.get(`/feedback/query/${queryId}`);
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    console.error('Failed to get query feedback:', error);
    throw error;
  }
}

/**
 * Get feedback statistics for a project
 */
export async function getProjectFeedbackStats(projectId: string): Promise<FeedbackStats> {
  const client = getAPIClient();
  
  try {
    const response = await client.get(`/feedback/project/${projectId}/stats`);
    return response.data;
  } catch (error: any) {
    console.error('Failed to get feedback stats:', error);
    throw new Error(`Failed to fetch feedback stats: ${error.response?.data?.detail || error.message}`);
  }
}

/**
 * Get recent issues for a project
 */
export async function getProjectIssues(
  projectId: string,
  limit: number = 20
): Promise<Array<any>> {
  const client = getAPIClient();
  
  try {
    const response = await client.get(`/feedback/project/${projectId}/issues`, {
      params: { limit }
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to get project issues:', error);
    throw error;
  }
}

/**
 * Mark a feedback issue as resolved
 */
export async function resolveFeedbackIssue(feedbackId: string): Promise<void> {
  const client = getAPIClient();
  
  try {
    await client.post(`/feedback/${feedbackId}/resolve`);
  } catch (error: any) {
    console.error('Failed to resolve feedback:', error);
    throw error;
  }
}

export default {
  submitFeedbackAPI,
  getQueryFeedback,
  getProjectFeedbackStats,
  getProjectIssues,
  resolveFeedbackIssue
};

